import { debounce } from 'lodash';

import { debounceInterval } from 'constants/debounceInterval';
import client from 'core/axios';

import { ensureArray } from 'utils/arrays';

export const populateFilters = filters => ({
  type: 'SCOPE_FILTER.POPULATE_FILTERS',
  filters,
});

export const clearFilters = () => ({
  type: 'SCOPE_FILTER.CLEAR_FILTERS',
});

export const setScopeFiltersOpen = ({ module, open }) => ({
  type: 'SCOPE_FILTER.SET_SCOPE_FILTER_OPEN',
  module,
  open,
});

export const toggleFilter = ({ group, id, label, source, ...rest }) => ({
  type: 'SCOPE_FILTER.TOGGLE_FILTER',
  group,
  id,
  label,
  source,
  ...rest,
});

export const removeFilter = ({ group }) => ({
  type: 'SCOPE_FILTER.REMOVE_FILTER',
  group,
});

export const addMultipleValuesForOneFilter = valueList => ({
  type: 'SCOPE_FILTER.ADD_MULTIPLE_VALUES_FOR_ONE_FILTER',
  valueList,
});

export const resetFilters = () => ({
  type: 'SCOPE_FILTER.RESET_FILTERS',
});

export const restoreFilters = selected => ({
  type: 'SCOPE_FILTER.RESTORE_FILTERS',
  selected,
});

export const populateManuallyCreatedCaseloads = caseloads => ({
  type: 'SCOPE_FILTER.POPULATE_MANUALLY_CREATED_CASELOADS',
  caseloads,
});

export const setManuallyCreatedCaseloadsLoading = loading => ({
  type: 'SCOPE_FILTER.SET_MANUALLY_CREATED_CASELOADS_LOADING',
  loading,
});

export const debouncedSetSearchTerm = debounce(
  ({ searchTerm, filter }, dispatch) =>
    dispatch({
      type: 'SCOPE_FILTER.SET_SEARCH_TERM',
      searchTerm,
      filter,
    }),
  debounceInterval,
);

export const setInputValue = ({ searchTerm, filter }) => ({
  type: 'SCOPE_FILTER.SET_INPUT_VALUE',
  searchTerm,
  filter,
});

export const setSearchTerm = filters => dispatch => {
  dispatch(setInputValue(filters));
  debouncedSetSearchTerm(filters, dispatch);
};

export const retrieveFilters =
  ({
    updateCachedContext = null,
    coursePlanner = null,
    ...customParams
  } = {}) =>
  async dispatch => {
    try {
      const endpoint = `v1/${
        coursePlanner ? 'cp-config' : 'sl_users'
      }/student-scope-context/`;

      const modeSpecificFilter = coursePlanner
        ? { student_scope: 'secondary_and_5th' }
        : {};

      const { status: allFiltersStatus, data: allFilters } = await client.get(
        endpoint,
        {
          params: {
            updateCachedContext,
            ...customParams,
            ...modeSpecificFilter,
          },
        },
      );
      const { status: schoolsFilterStatus, data: schoolsFilter } =
        await client.get('v1/sl-schools/schools/', {
          params: {
            fields: 'pk,name,school_type',
            student_scope: 'secondary',
            ...customParams,
          },
        });
      const filters = { ...allFilters, schools: schoolsFilter };
      if (allFiltersStatus === 200 && schoolsFilterStatus === 200) {
        dispatch(populateFilters(filters));
      }
    } catch (e) {
      console.warn('Failed to retrieve filtering options', e);
    }
  };

export const searchCaseloadCategories =
  ({ search, limit, offset }) =>
  async () => {
    try {
      const { status, data } = await client.get(`v1/roster/caseloads/`, {
        params: {
          exclude_personal: false,
          search,
          limit,
          offset,
        },
      });

      if (status === 200) {
        const rawResults = ensureArray(data?.results);
        const sections = rawResults.map(r => r.category.name);
        const uniqueSections = Array.from(new Set(sections));
        const typeHasResult = [];

        const results = rawResults.reduce((combinedResults, result) => {
          const type = result.category.name;

          const firstOfType = !typeHasResult.includes(type);

          const newCombinedResults = [
            ...combinedResults,
            {
              ...result,
              section: uniqueSections.indexOf(type),
              firstOfType,
              type,
            },
          ];

          if (firstOfType) typeHasResult.push(type);
          return newCombinedResults;
        }, []);
        return results;
      }
    } catch (e) {
      console.warn('Failed to search caseload categories', e);
    }
    return [];
  };

export const searchCaseloadsMobile =
  ({ search, limit, offset }) =>
  async () => {
    try {
      const { status, data } = await client.get(`v1/roster/caseloads/`, {
        params: {
          exclude_personal: false,
          search,
          limit,
          offset,
        },
      });
      if (status === 200) {
        return ensureArray(data?.results);
      }
    } catch (e) {
      console.warn('Failed to search caseload categories', e);
    }
    return [];
  };

export const retrieveManuallyCreatedCaseloads = () => async dispatch => {
  try {
    dispatch(setManuallyCreatedCaseloadsLoading(true));
    const { status, data } = await client.get(`v1/roster/caseloads/`, {
      params: {
        isSynced: false,
      },
    });
    if (status === 200) {
      dispatch(populateManuallyCreatedCaseloads(data));
    }
  } catch (e) {
    console.warn('Failed to retrieve manually created caseloads', e);
  }
  dispatch(setManuallyCreatedCaseloadsLoading(false));
};

export const restoreFilterFromStorage = () => (dispatch, getState) => {
  const localStorageFilters = JSON.parse(
    localStorage.getItem('selectedStudentScopeFilter') || '{}',
  );
  const {
    studentScope: { filters },
  } = getState();
  const existingLocalStorageFiltersKeys = Object.keys(localStorageFilters);
  const allStudentFilterKeys =
    Object.keys(filters) + ['caseloads', 'activities'];
  const studentOnlyFilters =
    existingLocalStorageFiltersKeys.reduce((agg, key) => {
      if (allStudentFilterKeys.includes(key)) {
        return {
          ...agg,
          [key]: localStorageFilters[key],
        };
      }
      return agg;
    }, {}) || {};
  dispatch(restoreFilters(studentOnlyFilters));
};

export const populateHasFetchedFilters = hasFetchedFilters => ({
  type: 'SCOPE_FILTER.POPULATE_HAS_FETCHED_FILTERS',
  hasFetchedFilters,
});

export const setHasFetchedFilters = hasFetchedFilters => dispatch => {
  dispatch(populateHasFetchedFilters(hasFetchedFilters));
};
